import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import { Minus, Plus } from 'lucide-react';
import { MobileFormPage } from '@/components/MobileFormPage';
import { ModalOverlay } from '@/components/ModalOverlay';
import { useToast } from '@/components/toastStore';
import { extractApiMessage } from '@/lib/api';
import { formatoDecimal } from '@/lib/liquidacion';
import { currentBundle } from '@/lib/offline/bundle';
import { useEntityId, useEntityMappings } from '@/lib/offline/operations';
import { useCamadaActiva, useCamadaDetalle } from '@/lib/queries/camadas';
import { useActualizarPesaje, useCrearPesaje, usePesajesDeLote } from '@/lib/queries/pesajes';
import type { Pesaje } from '@/lib/types';
import { useAuthStore } from '@/stores/auth';

function grams(raw: string): number | null {
  const normalized = raw.trim().replace(',', '.').replace(/\s+/g, '');
  if (!/^\d{1,2}(\.\d{1,3})?$/.test(normalized)) return null;
  const kg = Number(normalized);
  return kg > 0 && kg < 60 ? Math.round(kg * 1000) : null;
}
function inputWeight(value: number) {
  return (value / 1000).toFixed(3).replace('.', ',');
}
type Draft = { fecha: string; count: number; weights: string[]; recordId: number | null };
function sampleDraft(fecha: string, record: Pesaje | undefined): Draft {
  return {
    fecha,
    count: record?.pesos_g.length ?? 5,
    weights: record?.pesos_g.map(inputWeight) ?? Array<string>(5).fill(''),
    recordId: record?.id ?? null,
  };
}
function canonical(draft: Draft) {
  return {
    fecha: draft.fecha,
    weights: draft.weights.slice(0, draft.count).map((raw) => grams(raw) ?? raw.trim()),
  };
}
export function RegistrarPesajePage() {
  const { naveId: parameter } = useParams();
  const [search] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  useEntityMappings();
  const identity = [
    user?.id,
    user?.rol,
    parameter,
    search.get('pesaje'),
    search.get('camada'),
    search.get('lote'),
  ].join(':');
  const farm = currentBundle()?.explotacion_id ?? null;
  const context = useRef({ identity, farm, revision: 0 });
  if (context.current.identity !== identity) context.current = { identity, farm, revision: 0 };
  else if (farm != null) {
    if (context.current.farm != null && context.current.farm !== farm) context.current.revision++;
    context.current.farm = farm;
  }
  return <SampleForm key={identity + ':' + context.current.revision} parameter={parameter} />;
}
function SampleForm({ parameter }: { parameter: string | undefined }) {
  const [search] = useSearchParams();
  const naveId = useEntityId(parameter, 'naves'),
    requestedId = useEntityId(search.get('pesaje'), 'pesajes');
  const requestedCamada = useEntityId(search.get('camada'), 'camadas'),
    requestedLote = useEntityId(search.get('lote'), 'lotes');
  const contextual = search.has('camada') || search.has('lote');
  const activeQ = useCamadaActiva();
  const camadaId = contextual ? requestedCamada : (activeQ.data?.id ?? null);
  const detailQ = useCamadaDetalle(camadaId);
  const camada = detailQ.data?.camada;
  const lote = detailQ.data?.lotes.find(
    (item) => item.nave_id === naveId && (!contextual || item.lote_id === requestedLote),
  );
  const samplesQ = usePesajesDeLote(lote?.lote_id ?? null);
  const mappings = useEntityMappings();
  const mapId = (entity: string, id: number | null) =>
    mappings.find((item) => item.entity === entity && item.localId === id)?.remoteId ?? id;
  const records = samplesQ.data?.filter(
    (item) => mapId('lotes', item.lote_nave_id) === lote?.lote_id,
  );
  const requested = records?.find((item) => item.id === requestedId);
  const reliable =
    !!camada &&
    !!lote &&
    !!records &&
    !detailQ.isError &&
    !samplesQ.isError &&
    (contextual ? requestedCamada != null && requestedLote != null : !activeQ.isError);
  const initialReady = reliable && (!search.has('pesaje') || !!requested);
  const user = useAuthStore((s) => s.user),
    owner = user?.id;
  const scope = useRef({ owner, role: user?.rol, farm: currentBundle()?.explotacion_id ?? null });
  if (scope.current.farm == null) scope.current.farm = currentBundle()?.explotacion_id ?? null;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const current = () =>
    mounted.current &&
    useAuthStore.getState().user?.id === scope.current.owner &&
    useAuthStore.getState().user?.rol === scope.current.role &&
    (scope.current.farm == null || currentBundle()?.explotacion_id === scope.current.farm);
  const origin = useRef<{ camada: number; lote: number } | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null),
    [baseline, setBaseline] = useState<Draft | null>(null);
  const [pendingDate, setPendingDate] = useState<string | null>(null),
    [dateError, setDateError] = useState<string | null>(null);
  const today = format(new Date(), 'yyyy-MM-dd');
  useEffect(() => {
    if (!initialReady || draft || !camada || !lote) return;
    const fecha = requested?.fecha ?? today;
    const initial = sampleDraft(fecha, requested ?? records?.find((item) => item.fecha === fecha));
    origin.current = { camada: camada.id, lote: lote.lote_id };
    setDraft(initial);
    setBaseline(initial);
  }, [initialReady, draft, camada, lote, requested, records, today]);
  const sameContext =
    origin.current != null &&
    mapId('camadas', origin.current.camada) === camada?.id &&
    mapId('lotes', origin.current.lote) === lote?.lote_id;
  const dirty =
    !!draft &&
    !!baseline &&
    JSON.stringify(canonical(draft)) !== JSON.stringify(canonical(baseline));
  const currentRecord = records?.find((item) => item.fecha === draft?.fecha);
  const recordChanged =
    !!baseline &&
    (mapId('pesajes', baseline.recordId) !== (currentRecord?.id ?? null) ||
      (!!currentRecord &&
        JSON.stringify(currentRecord.pesos_g) !==
          JSON.stringify(baseline.weights.slice(0, baseline.count).map(grams))));
  const canWrite = (user?.rol === 'admin' || user?.rol === 'ganadero') && !camada?.cerrada;
  const ready = reliable && sameContext && !!draft;
  const create = useCrearPesaje(),
    update = useActualizarPesaje(
      lote?.lote_id ?? 0,
      mapId('pesajes', draft?.recordId ?? null) ?? 0,
    );
  const toast = useToast();
  const backTo =
    search.get('from') === 'listado' && camada && lote
      ? search.get('back') === 'nave'
        ? `/naves/${naveId}/muestreos?camada=${camada.id}`
        : '/camadas/' + camada.id + '/lotes/' + lote.lote_id + '/muestreos'
      : `/naves/${naveId}?${contextual && camada ? `camada=${camada.id}&` : ''}tab=pesos`;
  function selectDate(fecha: string) {
    const next = sampleDraft(
      fecha,
      records?.find((item) => item.fecha === fecha),
    );
    setDraft(next);
    setBaseline(next);
    setDateError(null);
    setPendingDate(null);
  }
  function requestDate(fecha: string) {
    if (!canWrite || !reliable || !sameContext) return;
    if (dirty) setPendingDate(fecha);
    else selectDate(fecha);
  }
  const activeWeights = draft?.weights.slice(0, draft.count) ?? [];
  const values = activeWeights.map(grams),
    valid = values.filter((value): value is number => value != null);
  const complete = values.length > 0 && values.every((value) => value != null);
  const average = valid.length
    ? valid.reduce((sum, value) => sum + value, 0) / valid.length / 1000
    : null;
  function changeCount(change: number) {
    setDraft((old) => {
      if (!old) return old;
      const count = Math.max(1, old.count + change);
      if (change > 0 && count > 200) return old;
      const weights = [...old.weights];
      while (weights.length < count) weights.push('');
      return { ...old, count, weights };
    });
  }
  async function save() {
    if (!ready || !canWrite || !draft || !current() || recordChanged) return;
    if (draft.recordId != null && !dirty) return backTo;
    const parsed = parseISO(draft.fecha);
    if (
      !/^\d{4}-\d{2}-\d{2}$/.test(draft.fecha) ||
      !isValid(parsed) ||
      format(parsed, 'yyyy-MM-dd') !== draft.fecha ||
      draft.fecha < camada.fecha_entrada ||
      draft.fecha > today
    ) {
      setDateError('Selecciona una fecha entre la entrada de la camada y hoy.');
      throw new Error('Revisa la fecha del muestreo.');
    }
    if (!complete) throw new Error('Completa todos los pesos, entre 0,001 y 59,999 kg.');
    const pesos_g = values as number[];
    await (draft.recordId != null
      ? update.mutateAsync({ pesos_g })
      : create.mutateAsync({
          lote_nave_id: lote.lote_id,
          body: { fecha: draft.fecha, pesos_g },
        }));
    if (!current()) return;
    toast.show({
      kind: 'success',
      message: 'Muestreo guardado.',
    });
    return backTo;
  }
  const loading = (!contextual && activeQ.isLoading) || detailQ.isLoading || samplesQ.isLoading;
  const failed = (!contextual && activeQ.isError) || detailQ.isError || samplesQ.isError;
  return (
    <MobileFormPage
      title={draft?.recordId != null ? 'Editar muestreo' : 'Muestreo de peso'}
      backTo={backTo}
      backLabel={search.get('from') === 'listado' ? 'Muestreos' : 'Nave'}
      dirty={dirty}
      disabled={!ready || !canWrite || recordChanged || !complete}
      saveLabel={
        draft?.recordId != null ? (dirty ? 'Guardar cambios' : 'Terminar') : 'Guardar muestreo'
      }
      onSave={save}
    >
      {camada && lote && (
        <div className="cm-form-context">
          <span>{lote.nave_codigo}</span>
          <span>{camada.codigo_lote}</span>
        </div>
      )}
      {failed ? (
        <p role="alert" className="parte-error">
          {extractApiMessage(
            samplesQ.error ?? detailQ.error ?? activeQ.error,
            'No se pudo cargar el muestreo.',
          )}
        </p>
      ) : loading ? (
        <p className="cm-form-note">Cargando muestreo…</p>
      ) : !initialReady && !draft ? (
        <p role="alert" className="parte-error">
          El muestreo o la asignación a la nave no está disponible.
        </p>
      ) : draft && !sameContext ? (
        <p role="alert" className="parte-error">
          La camada o la nave ha cambiado. Conserva tu borrador y revisa el contexto antes de
          continuar.
        </p>
      ) : null}
      {camada?.cerrada && (
        <p className="cm-form-note">Camada cerrada. El muestreo sólo se puede consultar.</p>
      )}
      {user?.rol !== 'admin' && user?.rol !== 'ganadero' && (
        <p className="cm-form-note">Tu perfil no permite modificar muestreos.</p>
      )}
      {recordChanged && (
        <div role="alert" className="parte-error">
          El registro de esta fecha ha cambiado. Cárgalo para revisarlo antes de guardar.
          {ready && canWrite && (
            <button
              type="button"
              className="ui-secondary mt-3"
              onClick={() => requestDate(draft!.fecha)}
            >
              Cargar registro del día
            </button>
          )}
        </div>
      )}
      {draft && (
        <fieldset disabled={!ready || !canWrite} className="cm-form-fields">
          <div className="cm-form-field">
            <label htmlFor="sample-date">Fecha del muestreo</label>
            <input
              id="sample-date"
              className="ui-field"
              type="date"
              value={draft.fecha}
              min={camada?.fecha_entrada}
              max={today}
              onChange={(e) => requestDate(e.target.value)}
              aria-invalid={!!dateError}
              aria-describedby={dateError ? 'sample-date-error' : undefined}
            />
            {dateError && (
              <p id="sample-date-error" className="text-warning-text">
                {dateError}
              </p>
            )}
          </div>
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm font-medium">Aves a pesar</span>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="ui-icon-button border border-border"
                aria-label="Quitar ave"
                disabled={draft.count <= 1}
                onClick={() => changeCount(-1)}
              >
                <Minus size={20} aria-hidden />
              </button>
              <strong className="min-w-8 text-center text-xl tabular-nums">{draft.count}</strong>
              <button
                type="button"
                className="ui-icon-button border border-border"
                aria-label="Añadir ave"
                disabled={draft.count >= 200}
                onClick={() => changeCount(1)}
              >
                <Plus size={20} aria-hidden />
              </button>
            </div>
          </div>
          <section>
            <h2 className="mb-3 text-sm font-medium">Pesos individuales · kg</h2>
            <div className="grid grid-cols-2 gap-3">
              {activeWeights.map((weight, index) => (
                <div key={index} className="cm-form-field min-w-0">
                  <label htmlFor={'sample-weight-' + index}>Pavo {index + 1}</label>
                  <input
                    id={'sample-weight-' + index}
                    type="text"
                    className="ui-field"
                    inputMode="decimal"
                    placeholder="0,000"
                    autoComplete="off"
                    value={weight}
                    aria-invalid={weight.trim() !== '' && values[index] == null}
                    aria-describedby={
                      weight.trim() !== '' && values[index] == null
                        ? 'sample-error-' + index
                        : undefined
                    }
                    onChange={(e) =>
                      setDraft(
                        (old) =>
                          old && {
                            ...old,
                            weights: old.weights.map((value, position) =>
                              position === index ? e.target.value : value,
                            ),
                          },
                      )
                    }
                  />
                  {weight.trim() !== '' && values[index] == null && (
                    <p id={'sample-error-' + index} className="text-warning-text">
                      Entre 0,001 y 59,999 kg, con hasta 3 decimales.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>
          <div className="cm-arrival-totals" aria-live="polite">
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm">Media de la muestra</span>
              <strong className="text-xl font-semibold tabular-nums">
                {average == null ? '—' : formatoDecimal(average, 3) + ' kg'}
              </strong>
            </div>
            <p className="cm-form-note">
              {valid.length} de {draft.count} aves pesadas
            </p>
          </div>
        </fieldset>
      )}
      {pendingDate != null && (
        <ModalOverlay
          ariaLabel="Cambiar muestreo"
          placement="bottom"
          className="max-w-md"
          onBackdrop={() => setPendingDate(null)}
        >
          <h2 className="ui-page-title">Cambiar muestreo</h2>
          <p className="py-3">
            Hay pesos sin guardar. Puedes seguir editando o descartarlos para cargar la fecha
            elegida.
          </p>
          <button type="button" className="ui-primary w-full" onClick={() => setPendingDate(null)}>
            Seguir editando
          </button>
          <button
            type="button"
            className="ui-secondary mt-2 w-full"
            onClick={() => selectDate(pendingDate)}
          >
            Descartar y continuar
          </button>
        </ModalOverlay>
      )}
    </MobileFormPage>
  );
}
export default RegistrarPesajePage;
